/**
 * Minimal markdown → HTML for Judgment Selling help panels.
 * Tables / paragraphs / lists only — not a full MD engine.
 */

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function inlineFmt(s: string): string {
  let out = escapeHtml(s);
  out = out.replace(/`([^`]+)`/g, "<code>$1</code>");
  out = out.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  out = out.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<span class="raid-help-link">$1</span>',
  );
  return out;
}

/**
 * Convert a SSOT help section (already marker-sliced) into compact HTML.
 */
export function renderHelpMarkdown(md: string): string {
  const preBlocks: string[] = [];
  const src = md
    .replace(/```mermaid\r?\n[\s\S]*?```/gi, "")
    .replace(/```[\s\S]*?```/g, (block) => {
      const inner = block.replace(/^```\w*\r?\n?/, "").replace(/```$/, "");
      const idx = preBlocks.length;
      preBlocks.push(
        `<pre class="raid-help-pre">${escapeHtml(inner.trim())}</pre>`,
      );
      return `\n%%PRE${idx}%%\n`;
    })
    .trim();
  if (!src) return "";
  const lines = src.split(/\r?\n/);
  const parts: string[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i] ?? "";
    const trimmed = line.trim();
    if (!trimmed) {
      i++;
      continue;
    }
    const preRef = trimmed.match(/^%%PRE(\d+)%%$/);
    if (preRef) {
      parts.push(preBlocks[Number(preRef[1])] ?? "");
      i++;
      continue;
    }
    if (/^#{1,4}\s+/.test(trimmed)) {
      const text = trimmed.replace(/^#{1,4}\s+/, "");
      parts.push(`<p class="raid-help-h">${inlineFmt(text)}</p>`);
      i++;
      continue;
    }
    if (trimmed.startsWith(">")) {
      const quote: string[] = [];
      while (i < lines.length && (lines[i] ?? "").trim().startsWith(">")) {
        quote.push((lines[i] ?? "").replace(/^\s*>\s?/, ""));
        i++;
      }
      parts.push(
        `<blockquote class="raid-help-quote">${quote.map((q) => `<p>${inlineFmt(q)}</p>`).join("")}</blockquote>`,
      );
      continue;
    }
    if (trimmed.startsWith("|")) {
      const rows: string[][] = [];
      while (i < lines.length && (lines[i] ?? "").trim().startsWith("|")) {
        const raw = (lines[i] ?? "").trim();
        i++;
        if (/^\|[\s\-:|]+\|$/.test(raw)) continue;
        const cells = raw
          .replace(/^\|/, "")
          .replace(/\|$/, "")
          .split("|")
          .map((c) => c.trim());
        rows.push(cells);
      }
      if (rows.length > 0) {
        const body = rows
          .map(
            (r, ri) =>
              `<tr>${r
                .map(
                  (c) =>
                    `<t${ri === 0 ? "h" : "d"}>${inlineFmt(c)}</t${ri === 0 ? "h" : "d"}>`,
                )
                .join("")}</tr>`,
          )
          .join("");
        parts.push(`<table class="raid-help-table">${body}</table>`);
      }
      continue;
    }
    if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
      const items: string[] = [];
      while (
        i < lines.length &&
        (/^\s*[-*]\s+/.test(lines[i] ?? "") || (lines[i] ?? "").trim() === "")
      ) {
        const l = (lines[i] ?? "").trim();
        i++;
        if (!l) continue;
        items.push(l.replace(/^[-*]\s+/, ""));
      }
      parts.push(
        `<ul class="raid-help-ul">${items.map((it) => `<li>${inlineFmt(it)}</li>`).join("")}</ul>`,
      );
      continue;
    }
    parts.push(`<p>${inlineFmt(trimmed)}</p>`);
    i++;
  }
  return parts.join("\n");
}

export function escapeAttr(s: string): string {
  return escapeHtml(s).replace(/'/g, "&#39;");
}
